import { useEffect, useState } from "react";
import filterFunction, { EFilterType } from "./filter";
import {
    Box,
    ListItemButton,
    Checkbox,
    ListItemText,
    Collapse,
    List,
    Select,
    SelectChangeEvent,
    MenuItem,
    Typography,
    ListItem as MuiListItem,
    styled,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { IColumn, TData, getValueToFilter } from "../DataTable";
import FilterInput from "../../../input/filter/FilterInput";

export type FieldType = "string" | "number" | "Date";

interface DataFilterProps<T> {
    filter: IFilter;
    OnFilter: (filter: IFilter) => void;
    rows: TData<T>[];
    column: IColumn<TData<T>>;
    filteredRows: TData<T>[];
}

export interface IFilter {
    type: EFilterType;
    request: string;
    isActive: boolean;
    field: string;
    isCaseSensitive?: boolean;
}

const ListItem = styled(MuiListItem)(({ theme }) => ({
    padding: 0,
    paddingLeft: theme.spacing(1),
}));

const DataFilter = <T extends object>({
    OnFilter,
    filter: dataFilter,
    rows,
    column,
    filteredRows
}: DataFilterProps<T>) => {
    const [filter, setFilter] = useState<IFilter>(dataFilter);
    const [filterTypes, setFilterTypes] = useState<EFilterType[]>([]);
    const { t } = useTranslation(["periodical"]);

    useEffect(() => {
        setFilterTypes(
            filterFunction.getSupport(
                getValueToFilter(rows[0], column.field, column)
            )
        );
    }, [filter]);

    useEffect(() => {
        OnFilter(filter);
    }, [filter]);

    return (
        <Box sx={{ marginBottom: 0.5 }}>
            <ListItemButton
                sx={{ padding: 0 }}
                onClick={() =>
                    setFilter((old) => ({ ...old, isActive: !old.isActive }))
                }
            >
                <Checkbox
                    sx={{ padding: 0, paddingLeft: 0.5 }}
                    checked={filter.isActive}
                    onChange={(_, checked) => {
                        setFilter((old) => ({
                            ...old,
                            isActive: checked,
                        }));
                    }}
                />
                <ListItemText>
                    <Typography variant="body2">{column.headerName}</Typography>
                </ListItemText>
            </ListItemButton>
            <Collapse in={filter.isActive}>
                <List>
                    <ListItem>
                        <Select
                            size="small"
                            value={filter.type}
                            onChange={(event: SelectChangeEvent) =>
                                setFilter((old) => ({
                                    ...old,
                                    type: event.target.value as EFilterType,
                                }))
                            }
                        >
                            {filterTypes.map((type) => (
                                <MenuItem key={type} value={type}>
                                    <Typography variant="body2">
                                        {t(`filter.${type}`)}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Select>
                    </ListItem>
                    <ListItem>
                        <FilterInput
                            options={Array.from(
                                new Set(
                                    filteredRows.flatMap(
                                        (row) =>
                                            getValueToFilter(
                                                row,
                                                column.field,
                                                column
                                            ) as string
                                    )
                                )
                            ).sort()}
                            value={filter.request}
                            onChange={(request, isCaseSensitive) => {
                                setFilter((old) => ({
                                    ...old,
                                    request,
                                    isCaseSensitive
                                }));
                            }}
                            isCaseSensitive={filter.isCaseSensitive || false}
                        />
                    </ListItem>
                </List>
            </Collapse>
        </Box>
    );
};

export default DataFilter;
